import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../../messages/messagesActions";
import { api } from "../../../api/api";
import { IBusinessPolicyToolFloater } from "./businesPolicyToolFloater.types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import {
  buildBinderRequest,
  ILeadScheduleScope,
} from "../leadBinder/leadBinderEndpoints";

// Feature: lead-binder-additional-tabs
// 30s client-side bound so lead-scoped fetch surfaces the error + retry path
// deterministically (Requirements 4.5, 4.9).
const LEAD_BINDER_REQUEST_TIMEOUT_MS = 30000;

// Guard preventing a duplicate lead-scoped save from being dispatched while a save for
// the same record is already in flight.
let leadToolFloaterSaveInFlight = false;

export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_PROGRESS =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_PROGRESS";
export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_SUCCESS =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_SUCCESS";
export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_FAILED =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_FAILED";

export const fetchBusinessPolicyToolFloaterListProgress = () =>
  action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_PROGRESS);
export const fetchBusinessPolicyToolFloaterListSuccess = (
  data: IBusinessPolicyToolFloater[]
) => action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_SUCCESS, { data });
export const fetchBusinessPolicyToolFloaterListFailed = () =>
  action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_LIST_FAILED);

export const fetchBusinessPolicyToolFloaterListAsync =
  (
    customerPolicyId: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyToolFloaterListProgress());

      let finalUrl = `/policy/get-policy-business-tool-floater?customer_policy_id=${customerPolicyId}`;

      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl);
      const data: IBusinessPolicyToolFloater[] = res.data.data;
      dispatch(fetchBusinessPolicyToolFloaterListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyToolFloaterListFailed());
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

export const addBusinesPolicyToolFloaterAsync =
  (
    data: IBusinessPolicyToolFloater,
    onCallback: (isSuccess: boolean, toolFloater?: IBusinessPolicyToolFloater) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(saveLoaderProgress());
      const res = await api.post(`/policy/edit-policy-business-tool-floater`, {
        ...data,
      });
      dispatch(
        showMessage({
          type: "success",
          message: "Tool Floater saved successfully!",
          displayAs: "snackbar",
        })
      );
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Tool Floater save. Persists with `lead_id` and NO `customer_policy_id`.
// A local in-flight guard prevents a duplicate save while one is already running. On
// success it re-fetches the list by the same `lead_id` (Requirements 4.2, 4.3). On
// failure/timeout it surfaces an error; the caller retains the entered input
// (Requirement 4.6). Mirrors `addLeadBusinessRiskLocationAsync`.
export const addLeadBusinessToolFloaterAsync =
  (
    data: IBusinessPolicyToolFloater,
    scope: ILeadScheduleScope,
    onCallback: (isSuccess: boolean, toolFloater?: IBusinessPolicyToolFloater) => void,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    if (leadToolFloaterSaveInFlight) {
      return;
    }
    leadToolFloaterSaveInFlight = true;
    try {
      dispatch(saveLoaderProgress());
      const request = buildBinderRequest(
        "tool-floater",
        { kind: "lead", leadId: scope.leadId },
        "save"
      );
      const {
        customer_id,
        customer_policy_id,
        ...rest
      } = data as IBusinessPolicyToolFloater & {
        customer_id?: unknown;
        customer_policy_id?: unknown;
      };
      const res = await api.post(
        request.url,
        { ...rest, ...request.body },
        { timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS }
      );
      dispatch(
        showMessage({
          type: "success",
          message: "Tool Floater saved successfully!",
          displayAs: "snackbar",
        })
      );
      // Re-fetch the list by the same lead_id after a successful save.
      dispatch(fetchLeadToolFloaterListAsync(scope, status));
      onCallback(true, res.data.data);
    } catch (err: any) {
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Tool Floater could not be saved. Please try again.",
          displayAs: "snackbar",
        })
      );
      onCallback(false);
    } finally {
      leadToolFloaterSaveInFlight = false;
      dispatch(saveLoaderCompleted());
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped Tool Floater list fetch. Keyed by `lead_id` (never `customer_policy_id`),
// routed through the centralized endpoint map. On failure/timeout it flips the list to
// the Failed load state (retry surfaced by the sub-tab) and shows an error message
// (Requirements 4.1, 4.5, 4.9). Mirrors `fetchLeadBusinessRiskListAsync`.
export const fetchLeadToolFloaterListAsync =
  (
    scope: ILeadScheduleScope,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyToolFloaterListProgress());
      const request = buildBinderRequest(
        "tool-floater",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      let finalUrl = request.url;
      if (status !== undefined && status !== "-1") {
        finalUrl += `&status=${status}`;
      }
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyToolFloater[] = res.data.data;
      dispatch(fetchBusinessPolicyToolFloaterListSuccess(data));
    } catch (err: any) {
      dispatch(fetchBusinessPolicyToolFloaterListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Tool Floater binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_PROGRESS =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_PROGRESS";
export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_SUCCESS =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_SUCCESS";
export const FETCH_BUSINESS_POLICY_TOOL_FLOATER_FAILED =
  "FETCH_BUSINESS_POLICY_TOOL_FLOATER_FAILED";

export const fetchBusinessPolicyToolFloaterProgress = () =>
  action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_PROGRESS);
export const fetchBusinessPolicyToolFloaterSuccess = (
  data: IBusinessPolicyToolFloater
) => action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_SUCCESS, { data });
export const fetchBusinessPolicyToolFloaterFailed = (errorMessage: string) =>
  action(FETCH_BUSINESS_POLICY_TOOL_FLOATER_FAILED, { errorMessage });

export const fetchBusinessPolicyToolFloaterAsync =
  (
    customerPolicyId: number,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyToolFloaterProgress());
      const res = await api.get(
        `/policy/get-policy-business-tool-floater?customer_policy_id=${customerPolicyId}&column=incremental_number&value=${incrementNumber}`
      );
      const data: IBusinessPolicyToolFloater[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyToolFloaterSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyToolFloaterFailed(
            "Oops! We couldn't find any records associated with your tool floater number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyToolFloaterFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message: err.response.data.message,
          displayAs: "snackbar",
        })
      );
    }
  };

// Feature: lead-binder-additional-tabs
// Lead-scoped single Tool Floater fetch (by incremental number). Keyed by `lead_id`
// (never `customer_policy_id`) via the centralized endpoint map. On failure/timeout it
// flips the record to the Failed load state so the sub-tab can surface the error +
// retry. Mirrors `fetchLeadBusinessRiskAsync`.
export const fetchLeadToolFloaterAsync =
  (
    scope: ILeadScheduleScope,
    incrementNumber: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      dispatch(fetchBusinessPolicyToolFloaterProgress());
      const request = buildBinderRequest(
        "tool-floater",
        { kind: "lead", leadId: scope.leadId },
        "fetch"
      );
      const finalUrl = `${request.url}&column=incremental_number&value=${incrementNumber}`;
      const res = await api.get(finalUrl, {
        timeout: LEAD_BINDER_REQUEST_TIMEOUT_MS,
      });
      const data: IBusinessPolicyToolFloater[] = res.data.data;
      if (data.length > 0) {
        dispatch(fetchBusinessPolicyToolFloaterSuccess(data[0]));
      } else {
        dispatch(
          fetchBusinessPolicyToolFloaterFailed(
            "Oops! We couldn't find any records associated with your tool floater number at the moment. Please ensure that the provided information is accurate and try again."
          )
        );
      }
    } catch (err: any) {
      dispatch(
        fetchBusinessPolicyToolFloaterFailed(ERROR_MESSAGES.SERVER_ERROR)
      );
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message ??
            "Tool Floater binder data could not be loaded.",
          displayAs: "snackbar",
        })
      );
    }
  };

export const CLEAR_BUSINES_POLICY_TOOL_FLOATER_STATE =
  "CLEAR_BUSINES_POLICY_TOOL_FLOATER_STATE";
export const CLEAR_BUSINES_POLICY_TOOL_FLOATER =
  "CLEAR_BUSINES_POLICY_TOOL_FLOATER";

export const clearBusinessToolFloaterRiskState = () =>
  action(CLEAR_BUSINES_POLICY_TOOL_FLOATER_STATE);

export const clearBusinessToolFloater = () =>
  action(CLEAR_BUSINES_POLICY_TOOL_FLOATER);
